/**
 * Client-side image compression used before uploading photos.
 *
 * Modern fast path:
 *   - Uses createImageBitmap(file) to decode directly from the File (no base64
 *     FileReader round-trip — saves ~100-300ms on large photos).
 *   - Runs the decode + canvas resize + JPEG encode in a Web Worker via
 *     OffscreenCanvas + convertToBlob, keeping the UI thread responsive.
 *   - Worker code is shipped inline via Blob URL so no Webpack/CRA worker
 *     loader plumbing is required.
 *
 * Fallback chain (slower, but always works):
 *   1. Try worker + OffscreenCanvas + createImageBitmap (modern)
 *   2. Main-thread createImageBitmap + canvas (skip FileReader)
 *   3. Legacy FileReader + Image() + canvas (pre-2017 browsers, e.g. old iOS)
 *
 * The PhotoCaptureSheet pre-fires compressImage as soon as the user picks the
 * file, so by the time they hit "Tallenna" the work is usually already done.
 */

const WORKER_CODE = `
self.onmessage = async (e) => {
  const { file, maxDim, quality } = e.data;
  try {
    // imageOrientation:'from-image' tells the browser to PHYSICALLY rotate
    // the bitmap pixels per the JPEG's EXIF Orientation tag before we draw
    // it on canvas. Without this, the bitmap is in raw-sensor orientation
    // and the resulting JPEG (which has no EXIF) renders rotated in PDF
    // print engines that don't honor EXIF (a known browser print bug).
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    let { width, height } = bitmap;
    if (width > maxDim || height > maxDim) {
      if (width > height) { height = Math.round((height / width) * maxDim); width = maxDim; }
      else { width = Math.round((width / height) * maxDim); height = maxDim; }
    }
    const canvas = new OffscreenCanvas(width, height);
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();
    const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality });
    self.postMessage({ ok: true, blob });
  } catch (err) {
    self.postMessage({ ok: false, error: (err && err.message) || 'compress error' });
  }
};
`;

let _workerSupported = null;
function workerSupported() {
  if (_workerSupported !== null) return _workerSupported;
  _workerSupported = (
    typeof Worker !== "undefined" &&
    typeof OffscreenCanvas !== "undefined" &&
    typeof createImageBitmap !== "undefined"
  );
  return _workerSupported;
}

async function compressInWorker(file, maxDim, quality) {
  const blob = new Blob([WORKER_CODE], { type: "application/javascript" });
  const url = URL.createObjectURL(blob);
  const worker = new Worker(url);
  try {
    const compressed = await new Promise((resolve, reject) => {
      worker.onmessage = (e) => {
        if (e.data?.ok) resolve(e.data.blob);
        else reject(new Error(e.data?.error || "worker compress error"));
      };
      worker.onerror = (err) => reject(err);
      worker.postMessage({ file, maxDim, quality });
    });
    return new File([compressed], file.name || "photo.jpg", { type: "image/jpeg" });
  } finally {
    worker.terminate();
    URL.revokeObjectURL(url);
  }
}

async function compressOnMainThread(file, maxDim, quality) {
  // Modern path: createImageBitmap skips the FileReader base64 round-trip.
  // imageOrientation:'from-image' guarantees EXIF is applied to pixels.
  if (typeof createImageBitmap !== "undefined") {
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    let { width, height } = bitmap;
    if (width > maxDim || height > maxDim) {
      if (width > height) { height = Math.round((height / width) * maxDim); width = maxDim; }
      else { width = Math.round((width / height) * maxDim); height = maxDim; }
    }
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, width, height);
    if (bitmap.close) bitmap.close();
    return await new Promise((resolve) =>
      canvas.toBlob(
        (b) => resolve(new File([b], file.name || "photo.jpg", { type: "image/jpeg" })),
        "image/jpeg",
        quality,
      ),
    );
  }
  // Legacy fallback for very old browsers
  return await new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width > height) { height = Math.round((height / width) * maxDim); width = maxDim; }
          else { width = Math.round((width / height) * maxDim); height = maxDim; }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (b) => resolve(new File([b], file.name || "photo.jpg", { type: "image/jpeg" })),
          "image/jpeg",
          quality,
        );
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  });
}

export async function compressImage(file, maxDim = 1600, quality = 0.82) {
  // Skip recompression for files that are already small — saves CPU + memory on
  // gallery-picked photos that have already been processed by another app.
  if (file && file.size > 0 && file.size < 350 * 1024) {
    return file;
  }
  if (workerSupported()) {
    try {
      return await compressInWorker(file, maxDim, quality);
    } catch {
      // Fall through to main thread on worker error
    }
  }
  return await compressOnMainThread(file, maxDim, quality);
}
